// src/lib/metricDefinitions.ts
//
// Static tooltip copy for the Oracle Financial Dashboard + Investment Memo.
// No AI, no fetch — pure constant. Both OverviewTab and MemoTab import from here
// so the definition shown on a dashboard tile and in the memo never drift.
//
// Each entry: `what` = plain-language definition; `use` = how a value (Buffett/Munger)
// investor reads it. Keep both to 1–2 sentences.
//
// Type-4 (Financial) entities substitute several metrics; TYPE4_OVERRIDES carries the
// replacement copy. Use getMetricDefinition(id, entityType) to resolve.

export type MetricId =
  | "marketCap"
  | "peTrailing"
  | "peForward"
  | "epsTrailing"
  | "epsForward"
  | "pegRatio"
  | "roe"
  | "roic"
  | "debtToEquity"
  | "pbRatio"
  | "divYield"
  | "fcfYield"
  | "debtToEbitda"
  | "freeCashFlow"
  | "wacc"
  | "roicWaccSpread"
  | "operatingMargin"
  | "shareholderYield"
  | "cashConversion"
  | "interestCoverage";

export interface MetricDefinition {
  /** Display label fallback (the tile supplies its own label; this is for reuse). */
  label: string;
  /** What the metric is. */
  what: string;
  /** How a value investor uses it. */
  use: string;
}

export const METRIC_DEFINITIONS: Record<MetricId, MetricDefinition> = {
  marketCap: {
    label: "Market Cap",
    what: "Total equity value at the current share price (price × shares outstanding).",
    use: "Sizes the business and gives context to absolute figures like Free Cash Flow. Not a valuation judgment on its own.",
  },
  peTrailing: {
    label: "Trailing P/E",
    what: "Share price divided by the last twelve months of earnings per share.",
    use: "A quick valuation read, but reported earnings can be distorted by one-offs, leverage, and accounting. Lean on FCF-based measures alongside it.",
  },
  peForward: {
    label: "Forward P/E",
    what: "Share price divided by consensus estimated EPS for the next fiscal year.",
    use: "Forward-looking valuation — only as reliable as the analyst estimates behind it.",
  },
  epsTrailing: {
    label: "Trailing EPS",
    what: "Net income attributable to shareholders over the last twelve months, per share.",
    use: "The earnings base for the trailing P/E. Watch for non-cash or one-time items that distort it.",
  },
  epsForward: {
    label: "Forward EPS",
    what: "Consensus analyst estimate of earnings per share for the next fiscal year.",
    use: "The earnings base for the forward P/E. Treat as a market expectation, not a fact.",
  },
  pegRatio: {
    label: "PEG Ratio",
    what: "Trailing P/E divided by the expected earnings growth rate.",
    use: "A growth-adjusted valuation screen (Peter Lynch's metric). Secondary in a quality-and-price framework — use it as a sanity check, not an anchor.",
  },
  roe: {
    label: "ROE",
    what: "Net income divided by shareholders' equity.",
    use: "Profitability on equity, but it can be inflated by leverage and buybacks. Read it next to Debt/Equity and ROIC, not alone.",
  },
  roic: {
    label: "ROIC",
    what: "After-tax operating profit (NOPAT) divided by invested capital (debt + equity − cash).",
    use: "The core quality metric — how efficiently the business turns capital into profit. Only meaningful against WACC: ROIC above WACC creates value, below it destroys value.",
  },
  debtToEquity: {
    label: "Debt/Equity",
    what: "Total debt divided by shareholders' equity.",
    use: "A leverage gauge; lower is generally safer. Durable compounders rarely depend on heavy debt.",
  },
  pbRatio: {
    label: "P/B Ratio",
    what: "Share price divided by book value per share.",
    use: "Most informative for asset-heavy businesses and financials. For asset-light compounders, book value understates intrinsic value — weight it lightly.",
  },
  divYield: {
    label: "Div Yield",
    what: "Annual dividend per share divided by share price.",
    use: "Cash returned via dividends — one half of total shareholder return, alongside buybacks.",
  },
  fcfYield: {
    label: "FCF Yield",
    what: "Free cash flow divided by market cap.",
    use: "The value investor's earnings yield — the cash the business generates relative to its price. Higher is cheaper, and often more reliable than the P/E because cash is harder to manipulate than reported earnings.",
  },
  debtToEbitda: {
    label: "Debt/EBITDA",
    what: "Total debt divided by EBITDA.",
    use: "Roughly how many years of cash earnings it would take to repay debt. Above ~3–4x warrants scrutiny outside capital-intensive sectors.",
  },
  freeCashFlow: {
    label: "Free Cash Flow",
    what: "Operating cash flow minus capital expenditures.",
    use: "The cash left after maintaining and growing the asset base — the raw material of owner earnings and the foundation of intrinsic value.",
  },
  wacc: {
    label: "WACC",
    what: "Weighted average cost of capital — the blended required return on the company's debt and equity, used as the DCF discount rate.",
    use: "The hurdle a business must clear to create value; compare ROIC against it. Model-derived from beta and capital structure, so it can vary slightly between fresh analyses.",
  },
  roicWaccSpread: {
    label: "ROIC − WACC Spread",
    what: "ROIC minus WACC.",
    use: "The clearest single value-creation signal: a positive spread means the business earns more on capital than that capital costs (it compounds); a negative spread means growth destroys value.",
  },
  operatingMargin: {
    label: "Operating Margin",
    what: "Operating income divided by revenue.",
    use: "A durability and pricing-power tell — high, stable operating margins often signal a moat. Watch the trend, not just the level.",
  },
  shareholderYield: {
    label: "Shareholder Yield",
    what: "Dividend yield plus net buyback yield (the rate of share-count reduction).",
    use: "Total cash returned to owners per dollar invested. Buybacks made below intrinsic value count as heavily as dividends.",
  },
  cashConversion: {
    label: "Cash Conversion",
    what: "Free cash flow divided by net income.",
    use: "A quality-of-earnings check — how much reported profit actually becomes cash. Persistently below ~80% can flag aggressive accounting or heavy reinvestment needs.",
  },
  interestCoverage: {
    label: "Interest Coverage",
    what: "EBIT divided by interest expense.",
    use: "How comfortably operating profit covers interest. Below ~3x signals elevated financial risk — a more direct solvency read than Debt/EBITDA alone.",
  },
};

/**
 * Type-4 (Financial: banks, insurers, float-driven businesses) substitutions.
 * For these entities, several standard metrics are not meaningful and are
 * replaced per ORACLE_METHODOLOGY Rule 0 / Type-4 archetype.
 */
export const TYPE4_OVERRIDES: Partial<Record<MetricId, MetricDefinition>> = {
  roic: {
    label: "ROTCE",
    what: "Return on tangible common equity — net income divided by tangible common equity.",
    use: "The return metric for banks and financials, where invested-capital ROIC is not meaningful.",
  },
  pbRatio: {
    label: "P/TBV",
    what: "Share price divided by tangible book value per share.",
    use: "The primary valuation anchor for banks and financials, where book value (less goodwill) is the relevant capital base.",
  },
  operatingMargin: {
    label: "Net Interest Margin / Efficiency",
    what: "Net interest margin (or the efficiency ratio) — the core profitability measures for a financial institution.",
    use: "Replaces operating margin for financials, whose 'operations' are spread- and cost-driven rather than product-margin-driven.",
  },
  wacc: {
    label: "Cost of Equity",
    what: "Required return on equity (DCF is suppressed for financials, so blended WACC does not apply).",
    use: "For financials, value is judged against cost of equity rather than a debt-weighted WACC; a tail-risk premium is added for pure insurers.",
  },
};

/**
 * Resolve the right definition for a metric given the entity type.
 * Pass the raw entityType string (e.g. "Type 4", "Type 6"); only Type 4 currently overrides.
 */
export function getMetricDefinition(
  id: MetricId,
  entityType?: string | null,
): MetricDefinition {
  const isType4 = (entityType || "").includes("Type 4");
  if (isType4 && TYPE4_OVERRIDES[id]) {
    return TYPE4_OVERRIDES[id] as MetricDefinition;
  }
  return METRIC_DEFINITIONS[id];
}
